import { FeatureLabel } from '@/components/marketing/info-tip';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  IconCheck, IconUsers, IconHandGrab, IconSparkles, IconChartBar, IconApi, IconAppWindow,
  IconGitMerge, IconMessages, IconFileSpreadsheet, IconDatabase, IconPalette, IconMail, IconWorld,
  IconGift, IconBolt, IconShieldCheck,
} from '@tabler/icons-react';
import { ArtPanel } from '@/components/marketing/art-panel';
import { FaqAccordion } from '@/components/marketing/faq-accordion';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { SeatsVsTicketsCalculator } from '@/components/marketing/warm/seats-vs-tickets';
import { BAND, Btn, Chip, Eyebrow, H2, IconTile, Lead, Muted, RevealSection } from '@/components/marketing/warm/primitives';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildOfferSchema, buildFaqPageSchema,
} from '@/lib/seo';

const creditMenu = [
  { action: 'Ticket created', cost: '1 credit', note: 'Any channel — dashboard, API, email, chat, portal' },
  { action: 'AI Analyst answer', cost: '1 credit', note: 'Ask questions about your ticket data in plain English' },
  { action: 'Kora AI draft reply', cost: '1 credit ($0.10)', note: 'No draft, no charge. Your team edits and sends; nothing is sent automatically' },
  { action: 'Kora AI confirmed resolution', cost: '3 credits ($0.30)', note: 'Charged when a customer confirms it solved the problem (or after 72 hours of quiet). Escalations and failures are free' },
  { action: 'Magic Helpdesk draft', cost: '10 credits', note: 'One run: AI scans your website and drafts 4–15 helpdesk articles' },
  { action: 'AI ticket structuring', cost: 'Free', note: 'Unlimited — paste raw text, get a clean ticket' },
  { action: 'Comments & collaboration', cost: 'Free', note: 'Unlimited comments, reactions, attachments' },
  { action: 'Public API responses', cost: '1 credit per 400', note: '20+ endpoints, webhooks included' },
  { action: 'File storage', cost: '2 credits / 2GB', note: 'Per month, on usage — 1MB already starts the first block' },
  { action: 'Helpdesk article', cost: '1 credit', note: 'Per published article, once — unlimited free edits, branding, analytics and subscriptions included' },
  { action: 'Customer portal', cost: 'Free to run — 1 credit per request created', note: 'Sign-in, browsing, replies, data export and deletion are free; only a new request costs a credit like any other ticket' },
  { action: 'Members', cost: 'Free', note: 'Invite your whole team — no per-seat fees' },
];

const includedFeatures = [
  'Unlimited members on every workspace',
  'Claim-based ticket ownership',
  'AI ticket structuring (unlimited, free)',
  'AI Analyst — text-to-SQL (1 credit/answer)',
  '20+ endpoint Public API + webhooks',
  'Embedded sessions (iframe without login)',
  'Ticket merge & bulk actions',
  'Realtime comments & file attachments',
  'CSV export & team analytics',
  'File storage metered at 2 credits / 2GB per month',
  'White-label workspace logo',
  'Email notifications',
  'EU data residency (Cloudflare D1)',
];

const FEATURE_ICONS = [
  IconUsers, IconHandGrab, IconSparkles, IconChartBar, IconApi, IconAppWindow, IconGitMerge,
  IconMessages, IconFileSpreadsheet, IconDatabase, IconPalette, IconMail, IconWorld,
];

const PLANS = [
  {
    icon: IconGift, name: 'Start free', big: '1,100', sub: 'free credits on signup',
    points: ['1,000 credits — valid 30 days', '100 credits — valid 10 years', 'Unlimited members, unlimited comments', 'No credit card required'],
    cta: { to: '/auth', label: 'Claim free credits' }, featured: false,
  },
  {
    icon: IconBolt, name: 'Pay as you go', big: '$1', bigSuffix: '= 10 credits', sub: 'top up from $5 · credits last 10 years',
    points: ['1 credit per ticket created (any channel)', '1 credit per AI Analyst answer', 'First recharge: +30% bonus credits', 'Suggested packs: $5 · $10 · $20 · $50 · $100', 'Wallet can overdraw to −10 credits — inbound never breaks'],
    cta: { to: '/auth', label: 'Get started' }, featured: true,
  },
  {
    icon: IconShieldCheck, name: 'Always included', big: '$0', sub: 'forever, in every workspace',
    points: includedFeatures.slice(0, 5),
    cta: { to: '/docs', label: 'Explore the API' }, featured: false,
  },
];

const faqs = [
  {
    q: 'What exactly is a credit?',
    a: 'A credit is Kora\'s unit of usage. $1 buys 10 credits. Creating a ticket costs 1 credit (10¢), and one AI Analyst answer costs 1 credit. Comments, members, the Public API (up to 400 responses per credit), and AI ticket structuring are free.',
  },
  {
    q: 'What does Kora AI cost?',
    a: 'Kora AI drafts replies for your team at 1 credit ($0.10) per draft, and its resolution agent answers portal and dashboard tickets at 3 credits ($0.30) per confirmed resolution. If Kora has no confident draft, or hands a ticket to a person, nothing is charged. There are no seats and no monthly fee.',
  },
  {
    q: 'What do I get when I sign up?',
    a: 'Every new workspace can claim 1,000 bonus credits (valid 30 days) plus 100 base credits (valid 10 years) — no credit card required. That\'s enough for roughly 1,000 tickets or a mix of tickets and AI Analyst answers.',
  },
  {
    q: 'Do credits expire?',
    a: 'Credits you purchase are valid for 10 years. The 1,000-credit signup bonus expires 30 days after you claim it, and the 100-credit welcome bonus lasts 10 years.',
  },
  {
    q: 'What is the minimum top-up?',
    a: '$5 — that\'s 50 credits (plus a 30% bonus on your first recharge: 65 credits). Suggested packs are $5, $10, $20, $50, and $100, or choose a custom amount. Prices are exclusive of tax.',
  },
  {
    q: 'Can my balance go negative?',
    a: 'Only slightly — down to −10 credits. This keeps your inbound channels working if you run dry mid-day. Once you\'re below the floor, new ticket creation and AI answers pause until you top up. Reading your data is never blocked.',
  },
  {
    q: 'How is this cheaper than per-seat helpdesks?',
    a: 'Per-seat tools charge $19–115 per agent every month whether tickets arrive or not. With Kora you pay 10¢ per ticket. A 10-agent team doing 300 tickets a month pays about $30 — the same team would pay $190–$1,150/mo on per-seat plans.',
  },
  {
    q: 'What happens if I stop paying?',
    a: 'Nothing is deleted. Your tickets, comments, and history stay intact and readable. You only need credits to create new tickets and run AI answers — top up whenever you\'re ready.',
  },
  {
    q: 'How do payments work?',
    a: 'Checkout and payments are handled by Dodo Payments (our merchant of record) — they support cards, UPI, and more, and handle tax compliance. Credits are added to your wallet automatically after payment.',
  },
];

export function PricingPage() {
  useEffect(() => {
    const title = 'Kora Pricing — Pay As You Go, No Per-Seat Fees';
    const description = 'Pay as you go: $1 = 10 credits, 1 credit per ticket, 1 per AI Analyst answer. 1,100 free credits. No seats, no monthly plan, no surprise bills.';
    setDocumentMeta(title, description);
    setCanonical('/pricing');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/pricing', title, description),
      buildFaqPageSchema(faqs),
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Kora',
        description: 'AI-powered issue tracking for modern ops teams — pay as you go with credits.',
        brand: { '@type': 'Brand', name: 'Kora' },
        offers: [
          buildOfferSchema(0, 'USD', 'Start free — claim 1,100 bonus credits (1,000 for 30 days + 100 for 10 years), no card required'),
          buildOfferSchema(5, 'USD', 'Credit packs from $5 — 10 credits per $1, +30% bonus credits on your first recharge'),
        ],
      },
    ]);

    return cleanup;
  }, []);
  return (
    <MarketingShell>
      <PageHero
        eyebrow="Pricing"
        title={<>Pay per ticket.<br /><Muted><span className="italic">Not per seat.</span></Muted></>}
        lead={<>Kora runs on credits: <strong style={{ color: 'var(--warm-text)', fontWeight: 500 }}>$1 = 10 credits</strong>, a ticket costs 1 credit. Invite your whole team free — you only pay for the work that happens.</>}
        actions={<>
          <Btn to="/auth">Claim free credits <ArrowRight className="size-3.5" /></Btn>
          <a href="#calculator" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm" style={{ color: 'var(--warm-muted)' }}>What would your team pay? →</a>
        </>}
        aside={
          <ArtPanel tint="pricing" className="p-6 md:p-10">
            <div className="max-w-sm mx-auto space-y-3">
              <div className="rounded-2xl p-6" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)', boxShadow: '0 20px 40px -28px rgba(28,26,21,0.35)' }}>
                <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>Per ticket</p>
                <p className="mt-2 flex items-baseline gap-2">
                  <span className="font-display font-light text-6xl leading-none" style={{ color: 'var(--warm-text)' }}>$0.10</span>
                  <span className="text-sm" style={{ color: 'var(--warm-muted)' }}>1 credit</span>
                </p>
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  {[['Members', 'Free'], ['AI structuring', 'Free'], ['SLAs', 'Free']].map(([k, v]) => (
                    <div key={k} className="rounded-xl py-2.5" style={{ background: 'var(--warm-inset)' }}>
                      <p className="text-[11px]" style={{ color: 'var(--warm-muted)' }}>{k}</p>
                      <p className="text-sm font-medium" style={{ color: 'var(--warm-text)' }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl px-5 py-4 flex items-center justify-between" style={{ background: 'var(--warm-inv)', color: 'var(--warm-inv-text)', border: '1px solid var(--warm-border)' }}>
                <span className="text-sm">Start with</span>
                <span className="font-display font-light text-2xl">1,100 free credits</span>
              </div>
            </div>
          </ArtPanel>
        }
      />

      <RevealSection className="!pt-0">
        <div className="reveal-stagger grid md:grid-cols-3 gap-4">
          {PLANS.map((p) => (
            <div key={p.name} className="rounded-2xl p-7 flex flex-col relative"
              style={p.featured
                ? { background: 'var(--warm-inv)', color: 'var(--warm-inv-text)', border: '1px solid var(--warm-border)' }
                : { background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
              {p.featured && <span className="absolute top-6 right-6"><Chip tone="neutral">How it works</Chip></span>}
              <div className="flex items-center gap-2.5 mb-5">
                <IconTile icon={p.icon} bg={p.featured ? 'var(--warm-inv-text)' : 'var(--warm-page)'} fg={p.featured ? '#1C1A15' : undefined} />
                <span className="font-medium">{p.name}</span>
              </div>
              <p className="flex items-baseline gap-2">
                <span className="font-display font-light text-5xl leading-none">{p.big}</span>
                {p.bigSuffix && <span className="text-base" style={{ opacity: 0.7 }}>{p.bigSuffix}</span>}
              </p>
              <p className="mt-2 text-sm" style={{ opacity: 0.7 }}>{p.sub}</p>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5">
                    <IconCheck size={16} stroke={2} className="shrink-0 mt-0.5" style={{ color: p.featured ? 'var(--warm-ok-on-inv)' : 'var(--warm-ok)' }} />
                    <span style={{ opacity: p.featured ? 0.9 : 1, color: p.featured ? undefined : 'var(--warm-muted)' }}>{pt}</span>
                  </li>
                ))}
              </ul>
              <Link to={p.cta.to}
                className="mt-7 inline-flex items-center justify-center gap-1.5 rounded-[10px] py-3 text-sm font-medium transition-opacity hover:opacity-90"
                style={p.featured
                  ? { background: 'var(--warm-inv-text)', color: '#1C1A15' }
                  : { border: '1px solid var(--warm-border)', color: 'var(--warm-text)', background: 'var(--warm-page)' }}>
                {p.cta.label} <ArrowRight className="size-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </RevealSection>

      <RevealSection style={BAND}>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Eyebrow>Credits</Eyebrow>
            <H2>What costs <br /><Muted>a credit?</Muted></H2>
            <Lead>Most of Kora is free. You pay for outcomes.</Lead>
            <p className="mt-4 text-sm" style={{ color: 'var(--warm-muted)' }}>
              See how the <Link to="/customer-portal-software" className="underline" style={{ color: 'var(--warm-text)' }}>customer portal</Link> works, or read about <Link to="/helpdesk-without-per-seat-pricing" className="underline" style={{ color: 'var(--warm-text)' }}>helpdesk without per-seat pricing</Link>.
            </p>
            <p className="mt-3 text-sm" style={{ color: 'var(--warm-muted)' }}>
              Learn more: <Link to="/ai-reply-drafting-software" className="underline" style={{ color: 'var(--warm-text)' }}>AI reply drafting</Link> · <Link to="/ai-customer-support-agent" className="underline" style={{ color: 'var(--warm-text)' }}>AI support agent</Link> · <Link to="/ai-helpdesk-pay-per-resolution" className="underline" style={{ color: 'var(--warm-text)' }}>Pay-per-resolution pricing</Link>
            </p>
          </div>
          <ul className="reveal-stagger lg:col-span-8 rounded-2xl overflow-hidden" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)' }}>
            {creditMenu.map((row, i) => {
              const free = row.cost.startsWith('Free');
              return (
                <li key={row.action} className="grid sm:grid-cols-12 gap-x-4 gap-y-1.5 px-5 py-4" style={i ? { borderTop: '1px solid var(--warm-border)' } : undefined}>
                  <span className="sm:col-span-4 text-sm font-medium" style={{ color: 'var(--warm-text)' }}>{<FeatureLabel label={row.action} />}</span>
                  <span className="sm:col-span-3"><Chip tone={free ? 'green' : 'blue'} wrap>{row.cost}</Chip></span>
                  <span className="sm:col-span-5 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{row.note}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </RevealSection>

      <RevealSection id="calculator">
        <Eyebrow>Cost calculator</Eyebrow>
        <H2>What would your <br /><Muted>team pay?</Muted></H2>
        <Lead>Drag to your monthly volume and compare with per-seat pricing.</Lead>
        <div className="mt-10">
          <SeatsVsTicketsCalculator />
        </div>
      </RevealSection>

      <RevealSection style={BAND}>
        <Eyebrow>Included</Eyebrow>
        <H2>Everything included, <br /><Muted>every workspace</Muted></H2>
        <Lead>No feature gates. No plan tiers. Credits are the only meter.</Lead>
        <ul className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {includedFeatures.map((f, i) => (
            <li key={f} className="flex items-center gap-3 rounded-xl p-4" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)' }}>
              <IconTile icon={FEATURE_ICONS[i]} bg="var(--warm-card)" />
              <span className="text-sm" style={{ color: 'var(--warm-text)' }}><FeatureLabel label={f} /></span>
            </li>
          ))}
        </ul>
      </RevealSection>

      <RevealSection>
        <Eyebrow>FAQ</Eyebrow>
        <H2>Frequently asked <br /><Muted>questions</Muted></H2>
        <Lead>Everything you need to know about credits.</Lead>
        <div className="mt-10">
          <FaqAccordion items={faqs.map((f) => ({ question: f.q, answer: f.a }))} twoCol />
        </div>
      </RevealSection>

      <CtaBand
        title={<>Claim your 1,100 <br /><Muted>free credits</Muted></>}
        lead="Start resolving issues in minutes. No credit card, no seat math, no surprise bills."
      />
    </MarketingShell>
  );
}
